import React, { useState, useEffect, useRef } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import fetchTimeout from '../fetchTimeout';

export default function SystemDetails({ extraDetails }) {
	const WS_URL = `ws${window.location.href.substring(4)}api/ws/solar/now`;

	const didUnmount = useRef(false);
	const [currentData, setCurrentData] = useState({});
	const [timeElapsed, setTimeElapsed] = useState(0);
	const { lastMessage, readyState } = useWebSocket(WS_URL, {
		shouldReconnect: (closeEvent) => {
			return didUnmount.current === false;
		},
		reconnectInterval: 5000,
		reconnectAttempts: 5
	});

	useEffect(() => {
		return () => {
			didUnmount.current = true;
		};
	}, []);

	useEffect(() => {
		if (lastMessage) {
			const data = JSON.parse(lastMessage?.data);

			setTimeElapsed(Math.ceil((data.timeNow - data.time) / 1000));
			setCurrentData(data);
		}
	}, [lastMessage]);

	async function fetchData() {
		const response = await fetchTimeout('api/solar/now', {}, 2000);
		const data = await response.json();

		setTimeElapsed(Math.ceil((data.timeNow - data.time) / 1000));
		setCurrentData(data);
	}

	useEffect(() => {
		fetchData();
	}, []);

	useEffect(() => {
		const interval = setInterval(() => {
			setTimeElapsed((oldTime) => oldTime + 1);
		}, 1000);
		return () => clearInterval(interval);
	}, [currentData]);

	/*useEffect(() => {
		if ((timeElapsed > 5 && timeElapsed < 60) || (timeElapsed > 60 && timeElapsed % 60 === 0)) fetchData();
	}, [timeElapsed]);*/

	const connectionStatus = {
		[ReadyState.CONNECTING]: 'kapcsolódás',
		[ReadyState.OPEN]: 'OK',
		[ReadyState.CLOSING]: 'nincs',
		[ReadyState.CLOSED]: 'nincs',
		[ReadyState.UNINSTANTIATED]: '??'
	}[readyState];

	return (
		<Card
			elevation={3}
			sx={{
				minWidth: 300,
				flexGrow: 1,
				maxWidth: 500,
				height: 'fit-content',
				margin: 1
			}}
		>
			{currentData?.data ? (
				<CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
					<Typography variant="p">Teljesítmény</Typography>
					<Typography variant="h3">{currentData?.data?.acPower?.toFixed(0)} W</Typography>
					<LinearProgress
						variant="determinate"
						value={Math.min(currentData?.data?.acPower / 50 ?? 0, 100)}
						sx={{ width: 1 / 1 }}
						color={currentData?.data?.acPower <= 5000 ? 'primary' : 'red'}
					/>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							flexWrap: 'wrap',
							marginTop: 1,
							width: 1 / 1
						}}
					>
						<Box sx={{ flexGrow: 1, flexBasis: 0 }}>
							<Typography sx={{ textAlign: 'center', fontSize: 12 }}>Ma</Typography>
							<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{(currentData?.data?.dayEnergy / 1000)?.toFixed(3)} kWh</Typography>
						</Box>
						<Box sx={{ flexGrow: 1, flexBasis: 0 }}>
							<Typography sx={{ textAlign: 'center', fontSize: 12 }}>Összesen</Typography>
							<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{(currentData?.data?.lifetimeEnergy / 1000000)?.toFixed(3)} MWh</Typography>
						</Box>
						<Box sx={{ flexGrow: 1, flexBasis: 0 }}>
							<Typography sx={{ textAlign: 'center', fontSize: 12 }}>Hőmérséklet</Typography>
							<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{currentData?.data?.temperature?.toFixed(2)} °C</Typography>
						</Box>
					</Box>
					{extraDetails && (
						<>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									flexWrap: 'wrap',
									marginTop: 2,
									width: 1 / 1
								}}
							>
								<Box sx={{ flexGrow: 1, flexBasis: 0 }}>
									<Typography sx={{ textAlign: 'center', fontSize: 12 }}>L1</Typography>
									<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{currentData?.data?.acVoltageL1?.toFixed(1)} V</Typography>
									<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{currentData?.data?.acCurrentL1?.toFixed(2)} A</Typography>
								</Box>
								<Box sx={{ flexGrow: 1, flexBasis: 0 }}>
									<Typography sx={{ textAlign: 'center', fontSize: 12 }}>L2</Typography>
									<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{currentData?.data?.acVoltageL2?.toFixed(1)} V</Typography>
									<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{currentData?.data?.acCurrentL2?.toFixed(2)} A</Typography>
								</Box>
								<Box sx={{ flexGrow: 1, flexBasis: 0 }}>
									<Typography sx={{ textAlign: 'center', fontSize: 12 }}>L3</Typography>
									<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{currentData?.data?.acVoltageL3?.toFixed(1)} V</Typography>
									<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{currentData?.data?.acCurrentL3?.toFixed(2)} A</Typography>
								</Box>
							</Box>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									flexWrap: 'wrap',
									marginTop: 2,
									width: 1 / 1
								}}
							>
								<Box sx={{ flexGrow: 1, flexBasis: 0 }}>
									<Typography sx={{ textAlign: 'center', fontSize: 12 }}>DC</Typography>
									<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{currentData?.data?.dcVoltage?.toFixed(1)} V</Typography>
									<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{currentData?.data?.dcCurrent?.toFixed(3)} A</Typography>
								</Box>
								<Box sx={{ flexGrow: 1, flexBasis: 0 }}>
									<Typography sx={{ textAlign: 'center', fontSize: 12 }}>AC</Typography>
									<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{currentData?.data?.acFrequency?.toFixed(2)} Hz</Typography>
									<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{currentData?.data?.acCurrent?.toFixed(2)} A</Typography>
								</Box>
								<Box sx={{ flexGrow: 1, flexBasis: 0 }}>
									<Typography sx={{ textAlign: 'center', fontSize: 12 }}>Status</Typography>
									<Typography sx={{ textAlign: 'center', fontSize: 20 }}>{currentData?.data?.statusText[0]}</Typography>
									{currentData?.data?.statusText[1] !== undefined && (
										<Typography sx={{ textAlign: 'center', fontSize: 16 }}>({currentData?.data?.statusText[1]})</Typography>
									)}
								</Box>
							</Box>
						</>
					)}
					{connectionStatus !== 'OK' ? (
						<Typography sx={{ marginTop: 1 }}>{timeElapsed ?? '--'} másodperce</Typography>
					) : (
						<Typography sx={{ marginTop: 1 }}>Kapcsolat: {connectionStatus ?? '--'}</Typography>
					)}
				</CardContent>
			) : (
				<CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
					<CircularProgress />
					<Typography sx={{ marginTop: 1 }}>Kapcsolat: {connectionStatus ?? '--'}</Typography>
				</CardContent>
			)}
		</Card>
	);
}
